import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0513 from "../lesson-13/lesson";

const linkLesson0513 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0513.id);

/* eslint-disable */
const theory = `<h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>In this lesson, you will learn about the objective parameters used in room acoustics, the way they are obtained and their meaning.<br class=newline>You will also learn about the terms and parameters used in subjective (perceptual) evaluation of acoustical quality of spaces designed for speech or music performance.<br class=newline>The link between the appropriate objective and subjective parameters (if any) shall be explained as well.
<h3 class=sectionHead>Introduction</h3>
<p>Performance spaces designed for speech, multiple purposes, but most especially for classical music, can be evaluated using objective measures and parameters derived mostly from acoustical impulse response measurements performed in such spaces.<br class=newline>However, musicians, speakers, actors and listeners will want to evaluate these spaces in terms of sensations and wording they can understand better than pure numbers an acoustician would normally use. It is vital that these groups have a common ground they can use to communicate with each other.<br class=newline>While the spaces designed for speech are usually subjectively evaluated in terms of speech intelligibility as the single most important perceptual dimension, the evaluation of concert halls and other performance spaces is a multidimensional task. As speech intelligibility is a complex phenomenon that is dependent not only on the acoustical properties of a room, but also on the noise in the room and the properties and orientation of the sound (speech) source, it will not be covered in this lesson, but more information can be found in <span class=cmti-10><a href=${linkLesson0513} target="_blank">Lesson 5.13</a></span> and in the Psychoacoustics course.
<h3 class=sectionHead>Objective parameters for audience areas</h3>
<p>Objective room acoustical parameters defined for the audience area can be grouped according to the subjective listening aspects they represent, as was done in ISO 3382. This is shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/11/theory/table-1.svg" data-img-alt="Table 1. Objective parameters for the audience area and the corresponding subjective listening aspects.">Table 1</a>.
<div class=table>
  <figure class=float>
    <figcaption class=caption><span class=id>Table 1. Objective parameters for the audience area and the corresponding subjective listening aspects.</span></figcaption>
    <img src="${config.ASSETS_URL}/05/11/theory/table-1.svg" alt="Table 1. Objective parameters for the audience area and the corresponding subjective listening aspects.">
  </figure>
</div>
<h4 class=subsectionHead>
  Sound strength
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>G</mi>
  </math>
</h4>
<p>
  <strong>
    <span class=cmbx-10>Sound strength </span>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>G</mi>
    </math>
  </strong>
  is a parameter that <strong><span class=cmbx-10>quantifies the support the hall will provide in terms of </span><span class=cmbx-10>amplification of sound, and is directly related to perceived loudness (level </span><span class=cmbx-10>of sound)</span></strong>.<br class=newline><strong><span class=cmbx-10>It is</span></strong> referred to as <strong><span class=cmbx-10>the logarithmic ratio of sound energy provided by a </span><span class=cmbx-10>source at a given measurement point in a room, and the sound energy </span><span class=cmbx-10>provided by the same source in free field at the distance of 10 </span><span class=cmbx-10>meters</span></strong>.<br class=newline>It can be measured and determined in different ways, using a calibrated omnidirectional source, in the sense that the source needs to provide sufficiently strong radiation of sound in the frequency range of interest (octave bands from 125 to 4000 Hz), and should be as omnidirectional as possible.<br class=newline><strong><span class=cmbx-10>If the measurements were made to yield impulse responses</span></strong>, sound strength can be determined as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <mi>G</mi>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msubsup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>10</mn>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(1)
</table>
<p>
  The impulse response represented by instantaneous sound pressure
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>p</mi>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  ) is measured at a given measurement point in the room (usually many of them), and the impulse response
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>p</mi>
      </mrow>
      <mrow>
        <mn>10</mn>
      </mrow>
    </msub>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  ) is measured in free field at the distance of 10 meters. Both are squared and integrated to obtain the total sound energy in the described conditions. Time instant
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  = 0 is the moment of the onset of direct sound, and
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  =
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>∞</mi>
  </math>
  is a point where the decay curve obtained from the impulse response has decreased by at least 30 dB.<br class=newline>In this type of measurement, the sound strength can also be expressed as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <mi>G</mi>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>pE</mi>
          </mrow>
        </msub>
        <mo class=MathClass-bin>−</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>pE</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>10</mn>
          </mrow>
        </msub>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(2)
</table>
<p>
  i.e., as the difference between the sound pressure exposure levels calculated from the two impulse responses.<br class=newline>Free-field conditions can be obtained in an anechoic room. If it is large enough, the impulse response and the corresponding sound pressure exposure level can be found directly by placing the microphone 10 meters away from the source. In small anechoic rooms the measurement can still be made at a distance
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>d</mi>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mo class=MathClass-rel>≥</mo>
  </math>
  3 m), but the obtained value of
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>G</mi>
  </math>
  must be corrected by subtracting 20*log(
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>d</mi>
  </math>
  /10) to get the final result.<br class=newline>As omnidirectional sources are not truly omnidirectional, the directivity of the source needs to be averaged by making several measurements around the source and calculating the energy mean of the obtained sound pressure exposure levels.<br class=newline>The free-field sound pressure exposure level can also be obtained from a measurement made in a reverberation room as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>pE</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>10</mn>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>pE</mi>
          </mrow>
        </msub>
        <mo class=MathClass-bin>+</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <mi>A</mi>
          </mrow>
          <mrow>
            <msub>
              <mrow>
                <mi>S</mi>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
            </msub>
          </mrow>
        </mfrac>
        <mo class=MathClass-bin>−</mo>
        <mn>37</mn>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(3)
</table>
<p>
  In the equation above,
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>L</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>pE</mi>
      </mrow>
    </msub>
  </math>
  is the sound-pressure exposure level measured in the reverberation room,
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>A</mi>
  </math>
  is the equivalent absorption area of the room that can be found from its volume and reverberation time using the Sabine formula, and
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>S</mi>
      </mrow>
      <mrow>
        <mn>0</mn>
      </mrow>
    </msub>
  </math>
  is unit area of 1 square meter.
    <br class=newline>The measurement of sound strength using impulse responses allows us to define two additional parameters named
  <strong>
    <span class=cmbx-10>early strength</span>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
        <mrow>
          <mi>G</mi>
        </mrow>
        <mrow>
          <mn>80</mn>
        </mrow>
      </msub>
    </math>
  </strong>
  (or
  <strong>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
        <mrow>
          <mi>G</mi>
        </mrow>
        <mrow>
          <mi mathvariant=italic>Early</mi>
        </mrow>
      </msub>
    </math>
  </strong>
  ) and
  <strong>
    <span class=cmbx-10>late </span><span class=cmbx-10>strength </span>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
        <mrow>
          <mi>G</mi>
        </mrow>
        <mrow>
          <mi mathvariant=italic>Late</mi>
        </mrow>
      </msub>
    </math>
  </strong>
  . Both parameters are calculated the same way as sound strength
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>G</mi>
  </math>
  , but
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>G</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>Early</mi>
      </mrow>
    </msub>
  </math>
  takes into account only the early part of the impulse response measured in the room, specifically, the first 80 ms of it, and is calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>G</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>Early</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mn>80</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msubsup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>10</mn>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(4)
</table>
<p>
  Analogously,
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>G</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>Late</mi>
      </mrow>
    </msub>
  </math>
  takes into account only the late part of the impulse response measured in the room, specifically, everything after 80 ms from the onset of direct sound, and is calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>G</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>Late</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>80</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msubsup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>10</mn>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(5)
</table>
<p><strong><span class=cmbx-10>If the measurements are made using a stationary omnidirectional </span><span class=cmbx-10>source</span></strong> (e.g. that emits constant pink noise), the sound strength can be determined rather easily as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <mi>G</mi>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>p</mi>
          </mrow>
        </msub>
        <mo class=MathClass-bin>−</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>p</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>10</mn>
          </mrow>
        </msub>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(6)
</table>
<p>
  i.e., as the difference between the sound pressure level
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>L</mi>
      </mrow>
      <mrow>
        <mi>p</mi>
      </mrow>
    </msub>
  </math>
  at a measurement point in the room and the sound pressure level
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>L</mi>
      </mrow>
      <mrow>
        <mi>p</mi>
        <mo class=MathClass-punc>,</mo>
        <mn>10</mn>
      </mrow>
    </msub>
  </math>
  measured in free field at 10 meters from the source. Both can be measured with a sound level meter capable of frequency analysis, or with any kind of equivalent equipment.<br class=newline>As before, the free field measurement can be made in an anechoic room by placing the microphone 10 meters away from the source. If the anechoic room is not large enough, the measurement can be made at the distance d from the source, and the obtained value of
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>G</mi>
  </math>
  must be corrected by subtracting 20*log(
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>d</mi>
  </math>
  /10) to get the final result.<br class=newline>It is also necessary to average the directivity of the source, as described above.<br class=newline><strong><span class=cmbx-10>If the sound power level of the omnidirectional source is known</span></strong>, the sound strength can be obtained as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <mi>G</mi>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>p</mi>
          </mrow>
        </msub>
        <mo class=MathClass-bin>−</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>W</mi>
          </mrow>
        </msub>
        <mo class=MathClass-bin>+</mo>
        <mn>31</mn>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(7)
</table>
<p>
  In the equation above,
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>L</mi>
      </mrow>
      <mrow>
        <mi>p</mi>
      </mrow>
    </msub>
  </math>
  is the sound pressure level measured at a measurement point in the room, and
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>L</mi>
      </mrow>
      <mrow>
        <mi>W</mi>
      </mrow>
    </msub>
  </math>
  is the sound power level of the source. The free field measurement is not necessary in this case.
<h4 class=subsectionHead>
  Early decay time
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=italic>EDT</mi>
  </math>
</h4>
<p><strong><span class=cmbx-10>The early decay time</span></strong> is an important objective parameter because it <strong><span class=cmbx-10>is a better </span><span class=cmbx-10>descriptor of perceived reverberance than the reverberation time</span></strong>, which is more related to the physical properties of the auditorium.<br class=newline>The early decay time is calculated from the slope of the decay curves obtained by integrating the measured impulse responses, or by directly measuring the decay using interrupted noise. The procedure is the same as for the reverberation time, but the slope is calculated from the linear regression line that best describes the initial decay from 0 to -10 dB.
<h4 class=subsectionHead>
  Clarity
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>C</mi>
      </mrow>
      <mrow>
        <mn>80</mn>
      </mrow>
    </msub>
  </math>
  and
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>C</mi>
      </mrow>
      <mrow>
        <mn>50</mn>
      </mrow>
    </msub>
  </math>
  , definition
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>D</mi>
      </mrow>
      <mrow>
        <mn>50</mn>
      </mrow>
    </msub>
  </math>
  , and centre time
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>T</mi>
      </mrow>
      <mrow>
        <mi>s</mi>
      </mrow>
    </msub>
  </math>
</h4>
<p>
  <strong><span class=cmbx-10>These parameters are all linked to perceived clarity of sound</span></strong>. The perceived clarity will always be higher if the ratio of the energy of direct (early) sound to the energy of reverberant (late) sound is higher.<br class=newline>To improve perceived clarity, the energy of early sound should be increased, and/or the energy of the late sound should be decreased.<br class=newline>Objective parameter
  <strong>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
        <mrow>
          <mi>C</mi>
        </mrow>
        <mrow>
          <mn>80</mn>
        </mrow>
      </msub>
    </math>
    <span class=cmbx-10>is called clarity or music clarity</span>
  </strong>
  , whereas the parameter
  <strong>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
        <mrow>
          <mi>C</mi>
        </mrow>
        <mrow>
          <mn>50</mn>
        </mrow>
      </msub>
    </math>
    <span class=cmbx-10>is </span><span class=cmbx-10>called speech clarity</span>
  </strong>
  . Both of them can be calculated from the measured impulse response as <strong><span class=cmbx-10>10 times the logarithm of the ratio of the energy of early sound </span><span class=cmbx-10>and the energy of late sound</span></strong>. The difference lies in the time instance
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>T</mi>
  </math>
  , which represents the boundary between the two.
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>C</mi>
          </mrow>
          <mrow>
            <mi>T</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>T</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
      </math>
    <td class=eq-no>(8)
</table>
<p>
  To calculate music clarity
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>C</mi>
      </mrow>
      <mrow>
        <mn>80</mn>
      </mrow>
    </msub>
  </math>
  , the time instance
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>T</mi>
  </math>
  is set to 80 ms, whereas for speech clarity
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>C</mi>
      </mrow>
      <mrow>
        <mn>50</mn>
      </mrow>
    </msub>
  </math>
  it is set to 50 ms. Any early reflection that arrives up to
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>T</mi>
  </math>
  milliseconds after direct sound will be attributed to early sound.<br class=newline>All directions of arrival of an early reflection are equally valid. The important thing is to increase the energy of early sound. Thus, an early reflection can arrive from above, from a lateral direction, or even from behind the listener, and improve the perceived clarity. This is also reflected in the measurements, as they are made using an omnidirectional microphone.<br class=newline>Another way to approach the problem is to calculate <strong><span class=cmbx-10>the ratio of energy of early </span><span class=cmbx-10>sound and the energy of total sound</span></strong>, which leads to the parameter called
  <strong>
    <span class=cmbx-10>definition</span>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
        <mrow>
          <mi>D</mi>
        </mrow>
        <mrow>
          <mn>50</mn>
        </mrow>
      </msub>
    </math>
  </strong>
  . It is calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>D</mi>
          </mrow>
          <mrow>
            <mn>50</mn>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mn>50</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(9)
</table>
<p>
  Speech clarity
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>C</mi>
      </mrow>
      <mrow>
        <mn>50</mn>
      </mrow>
    </msub>
  </math>
  and definition
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>D</mi>
      </mrow>
      <mrow>
        <mn>50</mn>
      </mrow>
    </msub>
  </math>
  are related as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>C</mi>
          </mrow>
          <mrow>
            <mn>50</mn>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <msub>
              <mrow>
                <mi>D</mi>
              </mrow>
              <mrow>
                <mn>50</mn>
              </mrow>
            </msub>
          </mrow>
          <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <msub>
              <mrow>
                <mi>D</mi>
              </mrow>
              <mrow>
                <mn>50</mn>
              </mrow>
            </msub>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(10)
</table>
<p>
  Another parameter in this group is the
  <strong>
    <span class=cmbx-10>centre time</span>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
        <mrow>
          <mi>T</mi>
        </mrow>
        <mrow>
          <mi>S</mi>
        </mrow>
      </msub>
    </math>
  </strong>
  , which basically represents <strong><span class=cmbx-10>the time instance of the centre of gravity of the </span><span class=cmbx-10>squared impulse response</span></strong>. In other words, the energy contained in the impulse response up to this time instance is equal to the energy contained in the impulse response after this time instance.<br class=newline>Centre time is calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>T</mi>
          </mrow>
          <mrow>
            <mi>S</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <mi>t</mi>
            <mo class=MathClass-bin>⋅</mo>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(11)
</table>
<p>It can be viewed as the indicator of the balance between perceived clarity and reverberance. Short centre time indicates higher clarity because the energy is concentrated in the early part of the impulse response. Long centre time indicates that the energy is spread over time, thus pointing to higher reverberance in the hall.
<h4 class=subsectionHead>
  Early lateral energy fraction
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>J</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>LF</mi>
      </mrow>
    </msub>
  </math>
  or
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>J</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>LFC</mi>
      </mrow>
    </msub>
  </math>
</h4>
<p>
  <strong><span class=cmbx-10>Early lateral energy fraction indicates the fraction of energy of lateral </span><span class=cmbx-10>reflections in the total energy of the sound</span></strong>. The components of sound are considered that arrive to the listening point within the first 80 ms after direct sound.<br class=newline>These parameters give <strong><span class=cmbx-10>an indication on the perceived or apparent width of </span><span class=cmbx-10>the sound source</span></strong>, in the sense that early lateral reflections will contribute to the perception of greater width.<br class=newline>To calculate these parameters, two impulse response measurements need to be made at each measurement point. One is made with an omnidirectional microphone, yielding
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>p</mi>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  ) as the instantaneous sound pressure of the obtained impulse response. Another is made with a figure-of-eight microphone, yielding
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>p</mi>
      </mrow>
      <mrow>
        <mi>L</mi>
      </mrow>
    </msub>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  ) as the instantaneous sound pressure of the obtained impulse response. The null of the figure-of-eight microphone points to the centre of the stage for large sources, or directly towards the source on stage, if the measurement is done with a single source.<br class=newline>The parameter
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>J</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>LF</mi>
      </mrow>
    </msub>
  </math>
  can be calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>J</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>LF</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>5</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
              <mrow>
                <mn>80</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msubsup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mi>L</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
              <mrow>
                <mn>80</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(12)
</table>
<p>
  The direct sound coming from the source is greatly reduced by the described microphone orientation, when measuring the impulse response with a figure-of-eight microphone. Moreover, it is completely excluded from the calculation of lateral energy (see the boundaries of the integral in the numerator in the equation above; time instant 0 is the onset of direct sound).<br class=newline>The contribution of individual lateral reflections varies greatly with the direction of arrival, due to the cosine polar pattern of the figure-of-eight microphone. To address this issue, the alternative parameter
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>J</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>LFC</mi>
      </mrow>
    </msub>
  </math>
  has been defined as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>J</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>LFC</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>5</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
              <mrow>
                <mn>80</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <mo class=MathClass-rel>|</mo>
            <msub>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mi>L</mi>
              </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>|</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
              <mrow>
                <mn>80</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(13)
</table>
<p>
  This parameter is considered to be in a better agreement with subjective perception than
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>J</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>LF</mi>
      </mrow>
    </msub>
  </math>
  .
<h4 class=subsectionHead>
  Late lateral sound level
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>L</mi>
      </mrow>
      <mrow>
        <mi>J</mi>
      </mrow>
    </msub>
  </math>
</h4>
<p>
  <strong><span class=cmbx-10>Late lateral sound level indicates the logarithmic ratio of energy of late </span><span class=cmbx-10>lateral reflections to the total energy of the sound measured at </span><span class=cmbx-10>the distance of 10 meters from the source in free field.</span></strong> Late lateral reflections arrive to the measurement point 80 ms or more after the direct sound.<br class=newline>To assess this, two impulse response measurements need to be made in each measurement point. One is made with an omnidirectional microphone at 10 meters from the source in free field, yielding
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>p</mi>
      </mrow>
      <mrow>
        <mn>10</mn>
      </mrow>
    </msub>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  ) as the instantaneous sound pressure of the obtained impulse response. Another is made with a figure-of-eight microphone at a given measurement point, yielding
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>p</mi>
      </mrow>
      <mrow>
        <mi>L</mi>
      </mrow>
    </msub>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  ) as the instantaneous sound pressure of the obtained impulse response. The null of the figure-of-eight microphone points to the centre of the stage for large combined sources such as an orchestra, or directly towards an individual source on the stage, if the measurement is done with a single source.<br class=newline>The parameter
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>L</mi>
      </mrow>
      <mrow>
        <mi>J</mi>
      </mrow>
    </msub>
  </math>
  can be calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>J</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>80</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msubsup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mi>L</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mi>∞</mi>
              </mrow>
            </msubsup>
            <msubsup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>10</mn>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(14)
</table>
<p>If needed, the single-number value is obtained through energy averaging of the values in octave bands from 125 Hz to 1 kHz.<br class=newline>Late lateral sound level is closely related to perceived envelopment by sound from the perspective of the listener who occupies the space at the measurement point.
<h4 class=subsectionHead>
  Bass ratio
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=italic>BR</mi>
  </math>
  and treble ratio
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=italic>TR</mi>
  </math>
</h4>
<p>
  Bass ratio and treble ratio are derived from the values of reverberation time in individual octave bands.<br class=newline>
  <strong>
    <span class=cmbx-10>Bass ratio </span>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>BR</mi>
    </math>
    <span class=cmbx-10>shows the relationship of reverberation time at low frequencies </span><span class=cmbx-10>and the reverberation time at middle frequencies</span>
  </strong>
  , and is calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <mi mathvariant=italic>BR</mi>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <mi>R</mi>
            <msub>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mn>125</mn>
              </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>R</mi>
            <msub>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mn>250</mn>
              </mrow>
            </msub>
          </mrow>
          <mrow>
            <mi>R</mi>
            <msub>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mn>500</mn>
              </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>R</mi>
            <msub>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mn>1000</mn>
              </mrow>
            </msub>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(15)
</table>
<p>
  <strong>
    <span class=cmbx-10>Treble ratio </span>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>TR</mi>
    </math>
    <span class=cmbx-10>evaluates the relationship of reverberation time at high frequencies </span><span class=cmbx-10>and the reverberation time at middle frequencies</span>
  </strong>
  , and is calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <mi mathvariant=italic>TR</mi>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <mi>R</mi>
            <msub>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mn>2000</mn>
              </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>R</mi>
            <msub>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mn>4000</mn>
              </mrow>
            </msub>
          </mrow>
          <mrow>
            <mi>R</mi>
            <msub>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mn>500</mn>
              </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>R</mi>
            <msub>
              <mrow>
                <mi>T</mi>
              </mrow>
              <mrow>
                <mn>1000</mn>
              </mrow>
            </msub>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(16)
</table>
<p>
  In the equations shown above,
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>R</mi>
    <msub>
      <mrow>
        <mi>T</mi>
      </mrow>
      <mrow>
        <mi>X</mi>
      </mrow>
    </msub>
  </math>
  is the value of reverberation time in the octave band with centre frequency X, in Hz.<br class=newline><strong><span class=cmbx-10>Bass ratio is related to subjective perception of warmth</span></strong> in the response of a hall, which is brought by longer reverberation at low frequencies.<br class=newline><strong><span class=cmbx-10>Treble ratio is related to subjective perception of brilliance</span></strong> in the response of a hall, which is brought by longer reverberation at high frequencies.
<h4 class=subsectionHead>
  Initial time delay gap
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=italic>ITDG</mi>
  </math>
</h4>
<p>
  <strong><span class=cmbx-10>The initial time delay gap is the time between the arrival of direct sound </span><span class=cmbx-10>and the first early reflection</span></strong> (reflection from the floor does not count) and is <strong><span class=cmbx-10>related with the subjective feeling of intimacy</span></strong>.<br class=newline>It can be deduced from the impulse response or the energy-time curve that stems from it.<br class=newline>In any kind of room, the value of
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=italic>ITDG</mi>
  </math>
  will strongly depend on the size of the room, the position of the source and the listener, and the presence or absence of reflecting surfaces. In general, lower
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=italic>ITDG</mi>
  </math>
  strengthens the feeling of intimacy. However, in a concert hall, the lowest values of
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=italic>ITDG</mi>
  </math>
  are usually in the back of the hall, while the highest ones are usually measured near the stage. This would indicate that the audience in the back will experience a more intimate involvement in the performance than the audience in the front, which seems contradictory by nature. The concert halls with average
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=italic>ITDG</mi>
  </math>
  of 20 ms are commonly evaluated as the best ones. Higher values are usually related to the sheer size of the hall, and for this reason (and many others) oversized halls will not be the optimal performance spaces. Within a hall, the seats closer to the stage will provide a more intimate experience, simply due to the proximity of the listener to the musicians.
<h3 class=sectionHead>Objective parameters for stage area</h3>
<p>Objective parameters defined for the stage area address the acoustic conditions in a concert hall or some other kind of performance space from the perspective of musicians. For them, the most important thing is to be able to hear themselves and each other, and to get a satisfactory response from the hall.<br class=newline>As before, acoustical parameters defined for the stage area can also be related to the specific subjective listening aspect they represent, as shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/11/theory/table-2.svg" data-img-alt="Table 2. Objective parameters for the stage area and the corresponding subjective listening aspects.">Table 2</a>.
<div class=table>
  <figure class=float>
    <figcaption class=caption><span class=id>Table 2. Objective parameters for the stage area and the corresponding subjective listening aspects.</span></figcaption>
    <img src="${config.ASSETS_URL}/05/11/theory/table-2.svg" alt="Table 2. Objective parameters for the stage area and the corresponding subjective listening aspects." />
  </figure>
</div>
<p>To emulate the conditions a musician will face while performing on stage, the measurements of these parameters are made on the stage area, with the source and microphone put close together. Specifically, an omnidirectional source and an omnidirectional microphone are put at the height of 1 m or 1.5 m to emulate a seated or a standing musician, respectively. The microphone is placed 1 m away from the acoustic centre of the source, i.e., quite close. The stage is normally filled with chairs and music stands, but the area around the measurement position should be clear (within the radius of 2 m).
<h4 class=subsectionHead>
  Early support
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>S</mi>
    <msub>
      <mrow>
        <mi>T</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>Early</mi>
      </mrow>
    </msub>
  </math>
</h4>
<p>Early support is the ratio of the energy of early reflections (within the first 100 ms after direct sound) to the energy of direct sound (floor reflection included in direct sound).<br class=newline>It is calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <mi>S</mi>
        <msub>
          <mrow>
            <mi>T</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>Early</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>20</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
              <mrow>
                <mn>100</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mn>10</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(17)
</table>
<p>
  As before,
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>p</mi>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  ) is the instantaneous sound pressure of the obtained impulse response. Early support is closely related to the (ease of) ensemble, i.e., it reflects how easily a musician will be able to hear other musicians in the orchestra.
<h4 class=subsectionHead>
  Late support
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>S</mi>
    <msub>
      <mrow>
        <mi>T</mi>
      </mrow>
      <mrow>
        <mi mathvariant=italic>Late</mi>
      </mrow>
    </msub>
  </math>
</h4>
<p>Late support is the ratio of the energy of late reflections (more than 100 ms after direct sound) to the energy of direct sound (floor reflection included in direct sound).<br class=newline>It is calculated as
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
        <mi>S</mi>
        <msub>
          <mrow>
            <mi>T</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>Late</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mi class=qopname>log</mi>
        <mo>⁡</mo>
        <mfrac>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>100</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
              <mrow>
                <mn>1000</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
          <mrow>
            <msubsup>
              <mrow>
                <mo>∫</mo>
              </mrow>
              <mrow>
                <mn>0</mn>
              </mrow>
              <mrow>
                <mn>10</mn>
                <mi mathvariant=italic>ms</mi>
              </mrow>
            </msubsup>
            <msup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
          </mrow>
        </mfrac>
        <mo class=MathClass-punc></mo>
      </math>
    <td class=eq-no>(18)
</table>
<p>
  As before,
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>p</mi>
  </math>
  (
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>t</mi>
  </math>
  ) is the instantaneous sound pressure of the obtained impulse response.<br class=newline>Late support is closely related to perceived reverberance from the viewpoint of a musician, i.e. it reflects the response of the hall as perceived by the musician.
<h3 class=sectionHead>Subjective parameters in perceptual evaluation of spaces</h3>
<p>Acoustical quality of a performance space designed for music is subjectively evaluated from many different points of view that can be called perceptual dimensions. The evaluation made taking into account only one or too few of these dimensions would most certainly be incomplete. For example, to judge a concert hall only by its reverberation would be a start but would not provide a complete picture. A comprehensive approach is required.<br class=newline>The literature often lists five basic perceptual dimensions defined primarily for evaluation of concert halls:
<ul class=itemize1>
  <li class=itemize>the <strong><span class=cmbx-10>clarity</span></strong> of sound, provided by the hall, should be high enough to facilitate the details in the music to be clearly heard
  <li class=itemize>the hall should have an appropriate <strong><span class=cmbx-10>reverberation</span></strong>, dependent on the type of music to be played in it
  <li class=itemize>the listener should have an <strong><span class=cmbx-10>impression of space</span></strong>, provided by the sound
  <li class=itemize>the experience of listening to a performance in the hall should evoke a feeling of <strong><span class=cmbx-10>intimacy</span></strong>
  <li class=itemize>the sound needs to have adequate <strong><span class=cmbx-10>loudness</span></strong> throughout the hall, so that it can be fully appreciated
</ul>
<h4 class=subsectionHead>Index of terms</h4>
<p><a href="#" data-img-src="${config.ASSETS_URL}/05/11/theory/table-3.svg" data-img-alt="Table 3. Glossary of terms used in subjective evaluation of acoustics in performance spaces.">Table 3</a> shows a set of terms usually used for describing the perceived quality of a performance space. The definitions of these terms are given as well. Some of the terms can be directly linked to corresponding objective parameters that can be measured and quantified, or to a physical property of the space. This is also indicated in the table, where applicable.
<figure>
  <figcaption class=caption><span class=id>Table 3. Glossary of terms used in subjective evaluation of acoustics in performance spaces.</span></figcaption>
  <img src="${config.ASSETS_URL}/05/11/theory/table-3.svg" alt="Table 3. Glossary of terms used in subjective evaluation of acoustics in performance spaces." />
</figure>
<h3 class=likesectionHead>References</h3>
<dl>
    <dt>[1]</dt><dd>ISO 3382-1:2009 – Acoustics – Measurement of room acoustics parameters – Part 1: Performance spaces</dd>
    <dt>[2]</dt><dd>Marshall Long, Architectural Acoustics, Chapter 19, Elsevier Academic Press, 2006</dd>
</dl>`;
/* eslint-enable */

export default theory;
